import React, { useEffect, useMemo, useState } from "react";
import { useOrderContext } from "../../hooks/useContext/useOrderContext";
import { Typography, Box, Button, TextField, Drawer, IconButton, RadioGroup, FormControlLabel, Radio, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SimpleAlert from '../Shared/Alert';
import { useAvailableItemConfigContext } from "../../hooks/useContext/useAvailableItemConfigContext";
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import { useTranslation } from "../../hooks/useTranslation";
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';


function FeatureCard(props) {
  const { dispatch } = useOrderContext();
  const theme = useTheme();
  const { t, language } = useTranslation();
  const { itemId, itemImage, itemName, itemType, itemDetails, itemIncludes, itemAvailable, itemSubCategory, expanded, onExpand, mobile, mobileVariant = 'row' } = props;
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showAlert, setShowAlert] = useState(false);
  const { openStatus } = useAvailableItemConfigContext();
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [showCustomize, setShowCustomize] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Get translated item name
  const displayItemName = language === 'pl' ? t(itemName) : itemName;

  const translateSize = (size) => {
    if (size === 'Small') return t('small');
    if (size === 'Large') return t('large');
    if (size === 'One Size') return t('oneSize');
    return size;
  };

  const handleExpandClick = () => {
    onExpand();
  };

  const defaultDetail = useMemo(() => {
    if (!Array.isArray(itemDetails) || itemDetails.length === 0) return null;
    return itemDetails.find((detail) => detail.itemSize === 'Large') || itemDetails[0];
  }, [itemDetails]);

  const effectiveSize = selectedSize || defaultDetail?.itemSize || '';
  const effectivePrice = (selectedPrice ?? defaultDetail?.itemPrice) ?? 0;

  useEffect(() => {
    if (!defaultDetail) return;
    setSelectedSize((prev) => prev || defaultDetail.itemSize);
    setSelectedPrice((prev) => (prev ?? defaultDetail.itemPrice));
  }, [defaultDetail]);

  const handleCustomizeClick = (event) => {
    event.stopPropagation();
    if (!expanded) {
      onExpand();
    }
    setShowCustomize((prev) => !prev);
  };

  const handleInstructionsChange = (event) => {
    // stop the card from expanding when typing
    event.stopPropagation();
    setSpecialInstructions(event.target.value);
  };

  const handleSizeChange = (size, event) => {
    setSelectedSize(size);
    const price = itemDetails.find(detail => detail.itemSize === size)?.itemPrice;
    setSelectedPrice(price);
    event.stopPropagation();
  };


  const incrementQuantity = (event) => {
    setQuantity(prev => prev + 1);
    event.stopPropagation();
  };


  const decrementQuantity = (event) => {
    setQuantity(prev => (prev > 1 ? prev - 1 : 1));
    event.stopPropagation();
  };


  const addItemToOrder = (event) => {
    dispatch({
      type: 'ADD_ORDER',
      payload: {
        _id: itemId,
        itemName,
        itemImage,
        itemType,
        itemSize: effectiveSize,
        itemPrice: effectivePrice,
        itemIncludes,
        itemAvailable,
        itemQuantity: quantity,
        itemSubCategory,
        specialInstructions,
      }
    });
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 1400)
    setQuantity(1);
    setSpecialInstructions('');
    setShowCustomize(false);
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
  };

  const baseDescription = useMemo(() => {
    if (!itemIncludes) return '';
    return String(itemIncludes);
  }, [itemIncludes]);

  const totalPrice = useMemo(() => {
    const price = Number(effectivePrice) || 0;
    return price * (Number(quantity) || 1);
  }, [effectivePrice, quantity]);

  const openSheet = (event) => {
    if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
    setSheetOpen(true);
  };

  const closeSheet = () => {
    setSheetOpen(false);
    setQuantity(1);
    setSpecialInstructions('');
    setShowCustomize(false);
  };

  const handleAddFromSheet = () => {
    addItemToOrder(null);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 1400);
    closeSheet();
  };


  if (mobile) {
    if (mobileVariant === 'card') {
      return (
        <Box>
          {openStatus === false && <SimpleAlert text={t('storeClosed')} showAlert={!openStatus} type={"error"} />}
          {showAlert && <SimpleAlert text={t('successfullyAddedToCart')} showAlert={showAlert} type={"success"} />}

          <Card
            sx={{
              background: theme.palette.component.componentBackground,
              borderRadius: '18px',
              border: `1px solid ${theme.palette.component.borderColor}`,
              overflow: 'hidden',
              height: 300,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {itemImage ? (
              <Box
                component="img"
                src={itemImage}
                alt={displayItemName}
                loading="lazy"
                decoding="async"
                sx={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.palette.common.white,
                  borderBottom: `1px solid ${theme.palette.component.borderColor}`,
                }}
              >
                <Typography variant="body2" sx={{ px: 1, textAlign: 'center', color: theme.palette.common.grey }}>
                  {baseDescription || t('none')}
                </Typography>
              </Box>
            )}

            <Box sx={{ p: 1.5, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {displayItemName}
              </Typography>

              {baseDescription ? (
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{
                    color: theme.palette.common.grey,
                    mt: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: 36,
                  }}
                >
                  {baseDescription}
                </Typography>
              ) : (
                <Box sx={{ minHeight: 36 }} />
              )}

              <Typography variant="subtitle1" sx={{ mt: 'auto', fontWeight: 800, lineHeight: 1.1 }}>
                {`${effectivePrice} ${t('currency')}`}
              </Typography>
            </Box>

            <Box sx={{ px: 1.5, pb: 1.5, mt: 'auto' }}>
              <Button
                onClick={openSheet}
                variant="outlined"
                sx={{
                  width: '100%',
                  height: 44,
                  borderRadius: '14px',
                  borderColor: theme.palette.component.borderColor,
                  color: theme.palette.common.offBlack,
                  textTransform: 'none',
                  backgroundColor: theme.palette.common.white,
                }}
                aria-label={t('addToCart')}
              >
                <AddRoundedIcon />
              </Button>
            </Box>
          </Card>

          <Drawer
            anchor="bottom"
            open={sheetOpen}
            onClose={closeSheet}
            PaperProps={{
              sx: {
                borderTopLeftRadius: '20px',
                borderTopRightRadius: '20px',
                backgroundColor: theme.palette.component.componentBackground,
              },
            }}
          >
            <Box sx={{ p: 2 }}>
              {itemImage ? (
                <Box
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.component.borderColor}`,
                    position: 'relative',
                  }}
                >
                  <Box
                    component="img"
                    src={itemImage}
                    alt={displayItemName}
                    loading="lazy"
                    decoding="async"
                    sx={{ width: '100%', height: 230, objectFit: 'cover', display: 'block' }}
                  />
                  <IconButton
                    onClick={closeSheet}
                    aria-label={t('close')}
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      backgroundColor: theme.palette.common.white,
                      border: `1px solid ${theme.palette.component.borderColor}`,
                      ':hover': { backgroundColor: theme.palette.common.white },
                    }}
                  >
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton onClick={closeSheet} aria-label={t('close')}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>
              )}

              <Typography variant="h5" sx={{ fontWeight: 800, mt: 1.5 }}>
                {displayItemName}
              </Typography>

              {baseDescription ? (
                <Typography variant="body2" gutterBottom sx={{ color: theme.palette.common.grey, mt: 0.5 }}>
                  {baseDescription}
                </Typography>
              ) : null}

              {Array.isArray(itemDetails) && itemDetails.length > 1 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                    {t('size')}:
                  </Typography>

                  <RadioGroup
                    value={effectiveSize}
                    onChange={(e) => {
                      const nextSize = e.target.value;
                      setSelectedSize(nextSize);
                      const price = itemDetails.find((d) => d.itemSize === nextSize)?.itemPrice;
                      setSelectedPrice(price ?? 0);
                    }}
                  >
                    {itemDetails.map((detail) => (
                      <Box
                        key={detail._id || detail.itemSize}
                        sx={{
                          border: `1px solid ${theme.palette.component.borderColor}`,
                          borderRadius: '14px',
                          px: 1,
                          py: 0.5,
                          mb: 1,
                          backgroundColor: theme.palette.common.white,
                        }}
                      >
                        <FormControlLabel
                          value={detail.itemSize}
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {translateSize(detail.itemSize)}
                              </Typography>
                              <Typography variant="body2" sx={{ color: theme.palette.common.grey }}>
                                {`${detail.itemPrice} ${t('currency')}`}
                              </Typography>
                            </Box>
                          }
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Box>
                    ))}
                  </RadioGroup>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                  {t('specialInstructions')}
                </Typography>
                <TextField
                  placeholder={t('addNote')}
                  fullWidth
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setQuantity((q) => (q > 1 ? q - 1 : 1))}
                    sx={{ minWidth: 44, borderRadius: '12px', borderColor: theme.palette.component.borderColor, color: theme.palette.common.offBlack }}
                  >
                    -
                  </Button>
                  <Typography variant="h6" sx={{ minWidth: 24, textAlign: 'center' }}>
                    {quantity}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setQuantity((q) => q + 1)}
                    sx={{ minWidth: 44, borderRadius: '12px', borderColor: theme.palette.component.borderColor, color: theme.palette.common.offBlack }}
                  >
                    +
                  </Button>
                </Box>

                <Button
                  variant="contained"
                  onClick={handleAddFromSheet}
                  disabled={openStatus === false}
                  sx={{
                    flex: 1,
                    height: 48,
                    borderRadius: '14px',
                    backgroundColor: theme.palette.common.offBlack,
                    color: theme.palette.common.white,
                    ':hover': { backgroundColor: theme.palette.common.offBlack },
                    textTransform: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 2,
                  }}
                >
                  <span>{t('add')}</span>
                  <span>{`${totalPrice} ${t('currency')}`}</span>
                </Button>
              </Box>
            </Box>
          </Drawer>
        </Box>
      );
    }

    return (
      <Box>
        {openStatus === false && <SimpleAlert text={t('storeClosed')} showAlert={!openStatus} type={"error"} />}
        {showAlert && <SimpleAlert text={t('successfullyAddedToCart')} showAlert={showAlert} type={"success"} />}

        <Card
          sx={{
            cursor: 'pointer',
            background: theme.palette.component.componentBackground,
            borderRadius: '16px',
            border: `1px solid ${theme.palette.component.borderColor}`,
            height: 132,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
            <Box
              sx={{
                width: 92,
                height: 92,
                borderRadius: '14px',
                overflow: 'hidden',
                flexShrink: 0,
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.component.borderColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {itemImage ? (
                <Box
                  component="img"
                  src={itemImage}
                  alt={displayItemName}
                  loading="lazy"
                  decoding="async"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Typography variant="body2" sx={{ px: 1, textAlign: 'center', color: theme.palette.common.grey }}>
                  {baseDescription || t('none')}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.common.offBlack }} noWrap>
                {displayItemName}
              </Typography>

              {baseDescription ? (
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{
                    color: theme.palette.common.grey,
                    mt: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: 36,
                  }}
                >
                  {baseDescription}
                </Typography>
              ) : (
                <Box sx={{ minHeight: 36 }} />
              )}

              <Typography variant="subtitle1" sx={{ mt: 'auto', fontWeight: 700, color: theme.palette.common.offBlack, lineHeight: 1.1 }}>
                {`${effectivePrice} ${t('currency')}`}
              </Typography>
            </Box>

            <IconButton
              onClick={openSheet}
              sx={{
                width: 44,
                height: 44,
                borderRadius: '14px',
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.component.borderColor}`,
              }}
              aria-label={t('addToCart')}
            >
              <AddRoundedIcon />
            </IconButton>
          </Box>
        </Card>

        <Drawer
          anchor="bottom"
          open={sheetOpen}
          onClose={closeSheet}
          PaperProps={{
            sx: {
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              backgroundColor: theme.palette.component.componentBackground,
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            {itemImage ? (
              <Box
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: `1px solid ${theme.palette.component.borderColor}`,
                  position: 'relative',
                }}
              >
                <Box
                  component="img"
                  src={itemImage}
                  alt={displayItemName}
                  loading="lazy"
                  decoding="async"
                  sx={{ width: '100%', height: 230, objectFit: 'cover', display: 'block' }}
                />
                <IconButton
                  onClick={closeSheet}
                  aria-label={t('close')}
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    backgroundColor: theme.palette.common.white,
                    border: `1px solid ${theme.palette.component.borderColor}`,
                    ':hover': { backgroundColor: theme.palette.common.white },
                  }}
                >
                  <CloseRoundedIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton onClick={closeSheet} aria-label={t('close')}>
                  <CloseRoundedIcon />
                </IconButton>
              </Box>
            )}

            <Typography variant="h5" sx={{ fontWeight: 800, mt: 1.5 }}>
              {displayItemName}
            </Typography>

            {baseDescription ? (
              <Typography variant="body2" gutterBottom sx={{ color: theme.palette.common.grey, mt: 0.5 }}>
                {baseDescription}
              </Typography>
            ) : null}

            {Array.isArray(itemDetails) && itemDetails.length > 1 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                  {t('size')}:
                </Typography>

                <RadioGroup
                  value={effectiveSize}
                  onChange={(e) => {
                    const nextSize = e.target.value;
                    setSelectedSize(nextSize);
                    const price = itemDetails.find((d) => d.itemSize === nextSize)?.itemPrice;
                    setSelectedPrice(price ?? 0);
                  }}
                >
                  {itemDetails.map((detail) => (
                    <Box
                      key={detail._id || detail.itemSize}
                      sx={{
                        border: `1px solid ${theme.palette.component.borderColor}`,
                        borderRadius: '14px',
                        px: 1,
                        py: 0.5,
                        mb: 1,
                        backgroundColor: theme.palette.common.white,
                      }}
                    >
                      <FormControlLabel
                        value={detail.itemSize}
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {translateSize(detail.itemSize)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.common.grey }}>
                              {`${detail.itemPrice} ${t('currency')}`}
                            </Typography>
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                    </Box>
                  ))}
                </RadioGroup>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                {t('specialInstructions')}
              </Typography>
              <TextField
                placeholder={t('addNote')}
                fullWidth
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => setQuantity((q) => (q > 1 ? q - 1 : 1))}
                  sx={{ minWidth: 44, borderRadius: '12px', borderColor: theme.palette.component.borderColor, color: theme.palette.common.offBlack }}
                >
                  -
                </Button>
                <Typography variant="h6" sx={{ minWidth: 24, textAlign: 'center' }}>
                  {quantity}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setQuantity((q) => q + 1)}
                  sx={{ minWidth: 44, borderRadius: '12px', borderColor: theme.palette.component.borderColor, color: theme.palette.common.offBlack }}
                >
                  +
                </Button>
              </Box>

              <Button
                variant="contained"
                onClick={handleAddFromSheet}
                disabled={openStatus === false}
                sx={{
                  flex: 1,
                  height: 48,
                  borderRadius: '14px',
                  backgroundColor: theme.palette.common.offBlack,
                  color: theme.palette.common.white,
                  ':hover': { backgroundColor: theme.palette.common.offBlack },
                  textTransform: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 2,
                }}
              >
                <span>{t('add')}</span>
                <span>{`${totalPrice} ${t('currency')}`}</span>
              </Button>
            </Box>
          </Box>
        </Drawer>
      </Box>
    );
  }

  return (
    <div>
      {openStatus === false && <SimpleAlert text={t('storeClosed')} showAlert={!openStatus} type={"error"} />}
      {showAlert && <SimpleAlert text={t('successfullyAddedToCart')} showAlert={showAlert} type={"success"} />}
      

      <Card onClick={handleExpandClick} sx={{ cursor: 'pointer', margin:".5rem", background: theme.palette.component.componentBackground }}>
        <CardHeader
          avatar={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
              <Typography variant="h2" color={theme.palette.common.offGrey} gutterBottom>
                {selectedPrice}
              </Typography>
              <Typography variant="h3" color={theme.palette.common.offGrey} gutterBottom>
                {t('currency')}
              </Typography>
            </Box>
          }
          //change font site of titl
          title={
            <Typography variant="h5" color={theme.palette.common.offGrey} gutterBottom>
              {displayItemName}
            </Typography>
          }
          // subheader={itemIncludes}
        />
          <Box sx={{ position: 'relative' }}>
            {itemImage && (
              <CardMedia
                component="img"
                height={expanded ? "max" : "194px"}
                image={itemImage}
                alt={`${itemName} - ${itemType === 'Food' ? 'Traditional Polish Food' : 'Beverage'} at Zielona Chatka Restaurant`}
                sx={{border: '1px solid #e0e0e0'}}
                loading="lazy"
                decoding="async"
              />
            )}
            
            {itemDetails.length > 1 && (
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'flex-end', 
                gap: '10px', 
                padding: '10px'
              }}>
                {itemDetails.map(detail => (
                  <Button 
                    key={detail._id} 
                    variant={selectedSize === detail.itemSize ? "primaryButton" : "greyButton" }
                    onClick={(event) => handleSizeChange(detail.itemSize, event)}
                    sx={{ border: '1px solid black', textTransform: 'none', opacity: selectedSize === detail.itemSize ? .8 : .6 }}
                  >
                    {translateSize(detail.itemSize)}
                  </Button>
                ))}
              </Box>
            )}

            {!itemImage && (
              <Box sx={{ display: 'flex', height: '194px', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ textAlign: "center", overflowWrap: 'break-word', whiteSpace: 'normal', padding: '0 20px'}}>
                  {itemIncludes}
                </Typography>
              </Box>
            )}
          </Box>

          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
              <Button sx={{ color: theme.palette.common.grey, borderColor: theme.palette.common.grey }} onClick={(event) => decrementQuantity(event)} >-</Button>
              <Typography variant="h6">{quantity}</Typography>
              <Button sx={{ color: theme.palette.common.grey, borderColor: theme.palette.common.grey }} onClick={(event) => incrementQuantity(event)} >+</Button>
            </Box>

            <Collapse in={Boolean(expanded && itemImage)} timeout="auto" unmountOnExit>
            <CardContent>
              <Typography sx={{ marginBottom: 2 }}>
                {itemIncludes ? itemIncludes : "No additional details"}
              </Typography>
            </CardContent>
          </Collapse>

            {/* Only show customize for food items */}
            {itemType === 'Food' && (
              <>
                <Box sx={{ mt: 2, ml: 1.5 }}>
                  <Typography
                    variant="body3"
                    sx={{
                      "&:hover": { color: theme.palette.common.blue },
                      textDecoration: 'underline',
                      fontStyle: "italic",
                      cursor: 'pointer',
                      color: theme.palette.common.grey,
                      textAlign: 'left',
                    }}
                    onClick={handleCustomizeClick}
                  >
                    {showCustomize ? t('hideCustomization') : t('customizeItem')}
                  </Typography>
                </Box>

                {/* Special instructions field */}
                {showCustomize && (
                  <Box sx={{ mt: 2 }} onClick={(e) => e.stopPropagation()}>
                    <TextField
                      label={t('specialInstructions')}
                      multiline
                      minRows={2}
                      fullWidth
                      value={specialInstructions}
                      onChange={handleInstructionsChange}
                    />
                  </Box>
                )}
              </>
            )}

            <Button onClick={(event) => addItemToOrder(event)} variant="primaryButton" sx={{ width: "100%", marginTop: "2rem", backgroundColor: theme.palette.primary.dark, color:theme.palette.common.white}}>
              {t('addToCart')}
            </Button>
          </CardContent>

      </Card>
    </div>
  );
}

export default FeatureCard;